// Reusable UI primitive components: Btn, Inp, Sel, Card, Modal, Badge, etc.
//
// These are the small building blocks composed throughout the app. Each
// is a thin wrapper around standard HTML elements with theme-token styling.
// All depend only on the theme constants and React; nothing in this file
// touches the application's data layer or routing.

import { useState, useEffect, useRef, CSSProperties } from "react";
import { T } from "../theme";

// Inject voice-input pulse animation styles once per page load. Runs as
// a top-level side effect because it has to register the keyframes
// before VoiceInput can use them.
if (typeof document !== 'undefined' && !document.getElementById('voice-pulse-style')) {
  const style = document.createElement('style');
  style.id = 'voice-pulse-style';
  style.textContent = '@keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.3; } 100% { opacity: 1; } } @keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

// ─── Badge ───
export const Badge = ({ children = undefined, c = undefined, b = undefined, size = "sm", dot = false }: { children?: any; c?: string; b?: string; size?: string; dot?: boolean }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: size === "sm" ? "2px 8px" : "4px 12px", borderRadius: 4, fontSize: size === "sm" ? 10 : 11, fontWeight: 600, color: c || T.txM, backgroundColor: b || T.bg4, letterSpacing: ".03em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
    {dot && <span style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: c }} />}
    {children}
  </span>
);

// ─── ConfBadge — confidence level indicator ───
export const ConfBadge = ({ level = undefined }: { level?: string }) => {
  const m: Record<string, { c: string; b: string; l: string }> = { high: { c: T.grn, b: T.grnS, l: "HIGH" }, medium: { c: T.ylw, b: T.ylwS, l: "MED" }, low: { c: T.red, b: T.redS, l: "LOW" } };
  const s = m[level || 'medium'] || m.medium;
  return <Badge c={s.c} b={s.b}>{s.l}</Badge>;
};

// ─── VoiceInput — push-to-talk speech recognition button ───
export const VoiceInput = ({ onTranscript = undefined, disabled = undefined }: { onTranscript?: (text: string) => void; disabled?: boolean }) => {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<any>(null);
  const recognitionRef = useRef<any>(null);
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) return null;
  const toggleListening = () => {
    setError(null);
    if (listening) { recognitionRef.current?.stop(); setListening(false); return; }
    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      let finalTranscript = '';
      recognition.onresult = (event: any) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) { finalTranscript += event.results[i][0].transcript + ' '; onTranscript?.(finalTranscript.trim()); }
          else { interim += event.results[i][0].transcript; onTranscript?.(finalTranscript + interim); }
        }
      };
      recognition.onerror = (e: any) => { console.error('[Voice] Error:', e.error); setError(e.error === 'not-allowed' ? 'Microphone permission denied' : e.error === 'no-speech' ? 'No speech detected' : `Error: ${e.error}`); setListening(false); };
      recognition.onend = () => setListening(false);
      recognitionRef.current = recognition;
      recognition.start();
      setListening(true);
    } catch (err: any) { console.error('[Voice] Start error:', err); setError('Speech recognition not available'); }
  };
  return (
    <button onClick={toggleListening} disabled={disabled} title={listening ? "Stop listening" : "Start voice input"} style={{ padding: "8px 10px", border: `1px solid ${listening ? T.red : T.brd}`, borderRadius: 6, backgroundColor: listening ? T.redS : "#fff", cursor: disabled ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .2s", position: "relative" }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={listening ? T.red : T.txM} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </svg>
      {listening && <span style={{ position: "absolute", top: -2, right: -2, width: 8, height: 8, borderRadius: "50%", backgroundColor: T.red, animation: "pulse 1.5s infinite" }} />}
      {error && <span style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", marginTop: 4, whiteSpace: "nowrap", fontSize: 9, color: T.red, backgroundColor: T.bg2, padding: "2px 6px", borderRadius: 3, border: `1px solid ${T.red}40`, zIndex: 10 }}>{error}</span>}
    </button>
  );
};

// ─── ChatExportButtons — export AI chat as PDF/DOCX/XLSX ───
// `disabled` exists so a caller that knows the answer did not finish can stop
// it being exported. An exported file leaves the product and gets read later,
// out of context, with no banner attached — so a partial answer is far more
// dangerous as a document than it is on screen. Optional and defaulted, so the
// other call sites (DraftingView, Repository) are unaffected.
export const ChatExportButtons = ({ content = '', disabled = false, disabledReason = 'This response is incomplete and cannot be exported' }: { content?: string; disabled?: boolean; disabledReason?: string }) => {
  const exportChat = async (format: string) => {
    if (disabled) return;
    try {
      const token = localStorage.getItem('kontractai_token');
      const res = await fetch('/api/extraction/chat-export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content, format }),
      });
      const blob = await res.blob();
      const ext = format === 'docx' ? 'docx' : format === 'pdf' ? 'pdf' : 'xlsx';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `AI_Chat_Export_${new Date().toISOString().split('T')[0]}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { alert('Export failed'); }
  };
  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
      {['pdf', 'docx', 'xlsx'].map(fmt => (
        <button key={fmt} type="button" disabled={disabled} title={disabled ? disabledReason : undefined} onClick={() => exportChat(fmt)} style={{ padding: '3px 8px', fontSize: 10, fontWeight: 500, border: `1px solid ${T.brd}`, borderRadius: 4, backgroundColor: '#fff', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1, fontFamily: 'inherit', color: T.txM, textTransform: 'uppercase' }}>{fmt}</button>
      ))}
    </div>
  );
};

// ─── Btn — primary action button with hover state ───
export const Btn = ({ children = undefined, v = "primary", sz = "md", onClick = undefined, disabled = undefined, full = undefined, style: cs = undefined }: { children?: any; v?: string; sz?: string; onClick?: any; disabled?: boolean; full?: boolean; style?: CSSProperties }) => {
  const [h, setH] = useState(false);
  const styles: Record<string, { bg: string; hbg: string; c: string; brd: string }> = { primary: { bg: T.acc, hbg: T.accH, c: "#fff", brd: "none" }, secondary: { bg: T.bg3, hbg: T.bgHov, c: T.tx, brd: `1px solid ${T.brd}` }, danger: { bg: T.redS, hbg: "rgba(208,64,64,.15)", c: T.red, brd: "none" }, ghost: { bg: "transparent", hbg: T.bg4, c: T.txM, brd: "none" }, success: { bg: T.grnS, hbg: "rgba(43,158,90,.15)", c: T.grn, brd: "none" } };
  const s = styles[v] || styles.primary;
  const pad = sz === "sm" ? "6px 14px" : sz === "lg" ? "13px 28px" : "10px 20px";
  // type="button" (not the implicit "submit") so a Btn can never accidentally
  // submit an ancestor <form> or be triggered by an Enter keypress in a sibling
  // input — hardening the route-action separation (Issue 2).
  return <button type="button" onClick={onClick} disabled={disabled} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: pad, borderRadius: 6, border: s.brd, fontSize: sz === "sm" ? 13 : 14, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? .5 : 1, backgroundColor: h && !disabled ? s.hbg : s.bg, color: s.c, transition: "all .12s", fontFamily: "inherit", width: full ? "100%" : undefined, ...cs }}>{children}</button>;
};

// ─── RefreshBtn — refresh action with "Updated" feedback state ───
export const RefreshBtn = ({ onClick = undefined, label = "Refresh", sz = "sm" }: { onClick?: any; label?: string; sz?: string }) => {
  const [state, setState] = useState("idle"); // idle | loading | done
  const timerRef = useRef<any>(null);
  const handleClick = async () => {
    if (state === "loading") return;
    setState("loading");
    try { await onClick?.(); } catch (e: any) { console.error("Refresh error:", e); }
    setState("done");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setState("idle"), 2000);
  };
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  return (
    <Btn v={state === "done" ? "success" : "secondary"} sz={sz} onClick={() => { handleClick(); }} disabled={state === "loading"}>
      {state === "loading" ? "Refreshing..." : state === "done" ? "Updated" : label}
    </Btn>
  );
};

// ─── Inp — labeled text input or textarea ───
export const Inp = ({ label = undefined, value = undefined, onChange = undefined, placeholder = undefined, type = "text", required = undefined, textarea = undefined, style: cs = undefined, small = undefined }: { label?: string; value?: string; onChange?: (v: string) => void; placeholder?: string; type?: string; required?: boolean; textarea?: boolean; style?: CSSProperties; small?: boolean }) => (
  <div style={{ marginBottom: small ? 12 : 16, ...cs }}>
    {label && <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.txD, marginBottom: 6, letterSpacing: ".04em", textTransform: "uppercase" }}>{label}{required && <span style={{ color: T.red }}> *</span>}</label>}
    {textarea ? <textarea value={value} onChange={e => onChange?.(e.target.value)} placeholder={placeholder} rows={3} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 14, resize: "vertical", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
    : <input type={type} value={value} onChange={e => onChange?.(e.target.value)} placeholder={placeholder} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />}
  </div>
);

// ─── Sel — labeled select dropdown ───
export const Sel = ({ label = undefined, value = undefined, onChange = undefined, options = undefined, required = undefined, placeholder = undefined, style: cs = undefined }: { label?: string; value?: string; onChange?: (v: string) => void; options?: any[]; required?: boolean; placeholder?: string; style?: CSSProperties }) => (
  <div style={{ marginBottom: 16, ...cs }}>
    {label && <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.txD, marginBottom: 6, letterSpacing: ".04em", textTransform: "uppercase" }}>{label}{required && <span style={{ color: T.red }}> *</span>}</label>}
    <select value={value} onChange={e => onChange?.(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: value ? T.tx : T.txD, fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box", cursor: "pointer" }}>
      <option value="">{placeholder || "Select..."}</option>
      {(options || []).map((o: any) => <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>{typeof o === "string" ? o : o.label}</option>)}
    </select>
  </div>
);

// ─── Modal — centered overlay with backdrop click-to-close ───
export const Modal = ({ open = false, onClose = undefined, title = undefined, children = undefined, w = 720 }: { open?: boolean; onClose?: () => void; title?: any; children?: any; w?: number }) => {
  if (!open) return null;
  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => { if (e.target === e.currentTarget) onClose?.(); };
  return (
    <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)" }} onMouseDown={handleBackdrop}>
      <div onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()} style={{ backgroundColor: T.bg2, borderRadius: 12, border: `1px solid ${T.brd}`, width: "92%", maxWidth: w, maxHeight: "88vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.15)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: `1px solid ${T.brd}` }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.tx }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.txM, fontSize: 20, cursor: "pointer", padding: "2px 6px", lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
};

// ─── Card — bordered container, optional onClick ───
export const Card = ({ children = undefined, style: cs = undefined, onClick = undefined }: { children?: any; style?: CSSProperties; onClick?: any }) => (
  <div onClick={onClick} style={{ backgroundColor: T.bg2, border: `1px solid ${T.brd}`, borderRadius: 10, padding: 20, cursor: onClick ? "pointer" : "default", transition: "border-color .12s", ...cs }}>{children}</div>
);

// ─── SectionTitle — large heading with optional subtitle and right slot ───
export const SectionTitle = ({ children = undefined, sub = undefined, right = undefined }: { children?: any; sub?: any; right?: any }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24 }}>
    <div>
      <h2 style={{ color: T.tx, fontSize: 26, fontWeight: 800, margin: "0 0 4px", letterSpacing: "-.02em" }}>{children}</h2>
      {sub && <p style={{ color: T.txM, fontSize: 14, margin: 0 }}>{sub}</p>}
    </div>
    {right && <div style={{ display: "flex", gap: 10, alignItems: "center" }}>{right}</div>}
  </div>
);

// ─── TabBar — horizontal tab navigation ───
export const TabBar = ({ tabs = [], active = undefined, onChange = undefined }: { tabs?: any[]; active?: string; onChange?: (id: string) => void }) => (
  <div className="kai-tab-bar" style={{ display: "flex", gap: 2, marginBottom: 20, borderBottom: `1px solid ${T.brd}`, width: "100%", maxWidth: "100%", overflowX: "auto", boxSizing: "border-box" }}>
    {tabs.map((t: any) => (
      <button key={t.id} onClick={() => onChange?.(t.id)} style={{ padding: "10px 18px", fontSize: 14, fontWeight: active === t.id ? 700 : 400, color: active === t.id ? T.acc : T.txM, backgroundColor: "transparent", border: "none", borderBottom: active === t.id ? `2px solid ${T.acc}` : "2px solid transparent", cursor: "pointer", fontFamily: "inherit" }}>
        {t.label}{t.count !== undefined && <span style={{ marginLeft: 6, fontSize: 11, padding: "2px 6px", borderRadius: 3, backgroundColor: active === t.id ? T.accS : T.bg4, color: active === t.id ? T.acc : T.txD }}>{t.count}</span>}
      </button>
    ))}
  </div>
);

// ─── SearchBar — search input with magnifier glyph ───
export const SearchBar = ({ value = "", onChange = undefined, placeholder = undefined }: { value?: string; onChange?: (v: string) => void; placeholder?: string }) => (
  <div style={{ position: "relative", marginBottom: 16 }}>
    <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: T.txD }}>⌕</span>
    <input value={value} onChange={e => onChange?.(e.target.value)} placeholder={placeholder || "Search..."} style={{ width: "100%", padding: "9px 10px 9px 30px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
  </div>
);

// ─── EmptyState — empty list/result placeholder ───
export const EmptyState = ({ icon = undefined, msg = undefined }: { icon?: any; msg?: any }) => (
  <Card style={{ textAlign: "center", padding: 40 }}>
    <div style={{ fontSize: 28, marginBottom: 8, opacity: .5 }}>{icon || "◇"}</div>
    <div style={{ fontSize: 13, color: T.txM }}>{msg}</div>
  </Card>
);
